import type {
  DashboardSaveV2Request,
  Validations,
  VendorOptionV2,
  Violation,
  VscProductV2,
} from "@credit-acceptance/step6-bff-client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useRef, useState } from "react";
import { useController, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Form } from "../../../../../components/ui/form";
import { SubsectionHeader } from "../../../../../components/ui/subsectionHeader";
import { formatCurrency } from "../../../../../lib/money";
import { getErrorMessage } from "../../../../../lib/query/errors";
import { unmappedMessages } from "../../../serverViolations";
import { useApplyServerFieldErrors } from "../../../useApplyServerFieldErrors";
import { useRegisterSaveableSection } from "../../../useRegisterSaveableSection";
import { useAncillaryForms } from "../api/useAncillaryForms";
import { buildFormOptions, buildVendorOptions } from "./ancillaryOptions";
import { CheckboxField, CurrencyField, DateField, DropdownField, TextField } from "./Fields";
import type { VscAccess } from "./productAccess";
import { type OriginalRow, ProductOriginalsModal } from "./ProductOriginalsModal";
import {
  getAncillaryFormWarning,
  getSaveViolations,
  translateVscViolations,
  withSaveViolations,
} from "./productViolations";
import {
  buildVscDefaultValues,
  FORM_NUMBER_SELECT_AN_OPTION,
  toVscSaveRequest,
  UNLIMITED_MILE_SENTINEL,
  UNLIMITED_MONTH_SENTINEL,
  vehicleServiceContractInformationSchema,
} from "./VehicleServiceContractInformation.schema";

interface VehicleServiceContractInformationProperties {
  readonly visible?: boolean;
  /** `additionalProducts.vsc` off the dashboard response. */
  readonly vsc?: VscProductV2;
  /** `additionalProducts.metadata.tpapVsc` — selects the third-party (TPAP) layout. */
  readonly tpapVsc?: boolean;
  /** `additionalProducts.options.vsc.vendors` — the dealer's TPAP vendors; absent means unknown, so the dropdown stays disabled. */
  readonly vendors?: readonly VendorOptionV2[];
  /** Resolved per-field editability (spec 6.2); defaults to everything locked. */
  readonly access?: VscAccess;
  /** `additionalProducts.contractVsc` — originally sold values shown in the P3 popup. */
  readonly contractVsc?: VscProductV2;
  readonly contractValuesUnavailable?: boolean;
}

const LOCKED: VscAccess = {
  headerLink: false,
  vendorDropdown: false,
  vendorEnabled: false,
  certificate: false,
  sellingPrice: false,
  terms: false,
  unlimited: false,
  effectiveDate: false,
  formNumber: false,
  anyEditable: false,
};

/**
 * RHF + Zod, same pattern as ContractVehicleInformation.tsx/InsuranceInformation.tsx
 * — see VehicleServiceContractInformation.schema.ts for the validation rules.
 *
 * Registered with the shared save orchestrator as "vsc", contributing only
 * `additionalProducts.vsc` — the orchestrator merges `additionalProducts` per
 * product, so this doesn't clobber the GAP section's contribution.
 */
export function VehicleServiceContractInformation({
  visible = true,
  vsc,
  vendors,
  tpapVsc: isTpapVsc = false,
  access = LOCKED,
  contractVsc,
  contractValuesUnavailable,
}: VehicleServiceContractInformationProperties) {
  const { t } = useTranslation();
  const [originalsOpen, setOriginalsOpen] = useState(false);
  const originalsTrigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!access.headerLink) {
      setOriginalsOpen(false);
    }
  }, [access.headerLink]);
  const form = useForm({
    resolver: zodResolver(vehicleServiceContractInformationSchema),
    mode: "onBlur",
    defaultValues: buildVscDefaultValues(vsc, isTpapVsc),
  });
  const { control } = form;
  const tpapVsc = useController({ control, name: "tpapVsc" });
  const vendorDisplay = useController({ control, name: "vendorDisplay" });
  const tpapVendor = useController({ control, name: "tpapVendor" });
  const certificateNumber = useController({ control, name: "certificateNumber" });
  const sellingPrice = useController({ control, name: "sellingPrice" });
  const termDisplay = useController({ control, name: "termDisplay" });
  const isUnlimitedMonth = useController({ control, name: "isUnlimitedMonth" });
  const termMonths = useController({ control, name: "termMonths" });
  const isUnlimitedMile = useController({ control, name: "isUnlimitedMile" });
  const termMiles = useController({ control, name: "termMiles" });
  const effectiveDate = useController({ control, name: "effectiveDate" });
  const formNumber = useController({ control, name: "formNumber" });

  const { unmapped, apply: applyServerErrors } = useApplyServerFieldErrors(form);
  const { getValues } = form;
  // The last dashboard/save validations, so a failed save's 422 findings render alongside them.
  // eslint-disable-next-line unicorn/no-useless-undefined -- required: this project's React types need an explicit initializer even for `T | undefined`
  const appliedValidations = useRef<Validations | undefined>(undefined);
  const applyVscFindings = useCallback(
    (saveViolations: Violation[] = []) =>
      applyServerErrors(
        translateVscViolations(withSaveViolations(appliedValidations.current, saveViolations), {
          tpap: getValues("tpapVsc"),
        })
      ),
    [applyServerErrors, getValues]
  );
  const vscValidationsKey = JSON.stringify(vsc?.validations);
  useEffect(() => {
    appliedValidations.current = vsc?.validations;
    applyVscFindings();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on stringified content, same as Signers' useServerFieldErrors
  }, [vscValidationsKey]);
  // The TPAP layout decides which fields render, so a layout change re-routes the same findings.
  useEffect(() => {
    applyVscFindings();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only the layout change should re-route
  }, [tpapVsc.field.value]);

  const [saveError, setSaveError] = useState<string>();
  const [formWarning, setFormWarning] = useState<string>();

  useRegisterSaveableSection(
    "vsc",
    {
      // Pre-approval and locked cards skip the required-but-empty highlighting (spec 5.1).
      validate: async () => !access.anyEditable || form.trigger(),
      getPayload: (): Partial<DashboardSaveV2Request> =>
        // Nothing to write for a locked card, or when the deal has no VSC and isn't a TPAP one.
        !visible || !access.anyEditable || !(vsc || isTpapVsc)
          ? {}
          : { additionalProducts: { vsc: toVscSaveRequest(form.getValues(), vsc) } },
      onSaved: ({ metadata, additionalProducts, refreshFailed }) => {
        setSaveError(undefined);
        setFormWarning(getAncillaryFormWarning(metadata));
        // The save response is the source of truth, rejected and absent values included.
        if (refreshFailed) {
          form.reset(undefined, { keepValues: true, keepDirty: false, keepErrors: true });
        } else {
          form.reset(
            buildVscDefaultValues(additionalProducts?.vsc, additionalProducts?.metadata?.tpapVsc ?? isTpapVsc),
            {
              keepErrors: true,
            }
          );
        }
        // After the reset, so the findings route against the saved layout; a clean or absent VSC clears them.
        appliedValidations.current = additionalProducts?.vsc?.validations;
        applyVscFindings();
      },
      onSaveFailed: (error) => {
        const failed = getSaveViolations(error, "vsc");
        applyVscFindings(failed);
        setSaveError(failed.length > 0 ? undefined : getErrorMessage(error));
      },
    },
    visible
  );

  // Disabled while the tab is read-only (e.g. another analyst holds the lock) or when the BFF supplied no vendor list, never empty.
  const tpapVendorOptions = buildVendorOptions(vendors, { id: vsc?.vendorId, name: vsc?.vendorName });
  const vendorDropdownEnabled = access.vendorEnabled && !!vendors?.length;
  const selectedVendorId = tpapVendor.field.value ? Number(tpapVendor.field.value) : undefined;
  const { data: forms } = useAncillaryForms("VSC", selectedVendorId, tpapVsc.field.value && access.formNumber);
  const formNumberOptions = buildFormOptions(
    forms,
    // The saved form belongs to the saved vendor, so it only stays selectable while that vendor is.
    selectedVendorId === vsc?.vendorId ? vsc?.formNo : undefined,
    {
      selectAnOption: t("vehicle.vehicleServiceContract.selectAnOption"),
      formNotListed: t("vehicle.vehicleServiceContract.formNotListed"),
    },
    FORM_NUMBER_SELECT_AN_OPTION
  );

  // A vendor's forms don't carry over to another vendor: reload them and reset the selection, flagged required straight away (caps2 parity).
  function handleVendorChange(value: string) {
    tpapVendor.field.onChange(value);
    formNumber.field.onChange(FORM_NUMBER_SELECT_AN_OPTION);
    void form.trigger("formNumber");
  }

  // Validates on change rather than blur (like CPO Date) — selecting an
  // option doesn't blur the trigger, so `mode: "onBlur"` alone would never
  // reveal the required error.
  function handleFormNumberChange(value: string) {
    formNumber.field.onChange(value);
    void form.trigger("formNumber");
  }

  // Checking sets Term Months to the "999" sentinel; unchecking wipes
  // whatever was typed back to blank.
  function handleUnlimitedMonthChange(checked: boolean) {
    isUnlimitedMonth.field.onChange(checked);
    termMonths.field.onChange(checked ? UNLIMITED_MONTH_SENTINEL : "");
    void form.trigger("termMonths");
  }

  // Same wipe-on-uncheck behavior as the month checkbox, sentinel "999999".
  function handleUnlimitedMileChange(checked: boolean) {
    isUnlimitedMile.field.onChange(checked);
    termMiles.field.onChange(checked ? UNLIMITED_MILE_SENTINEL : "");
    void form.trigger("termMiles");
  }

  const originalRows: OriginalRow[] = [
    { label: t("vehicle.vehicleServiceContract.vendor"), value: contractVsc?.vendorName },
    {
      label: t("vehicle.vehicleServiceContract.sellingPrice"),
      value: contractVsc?.sellingPrice == null ? undefined : formatCurrency(contractVsc.sellingPrice),
    },
    {
      label: t("vehicle.vehicleServiceContract.termMonths"),
      value: contractVsc?.unlimitedMonths ? t("vehicle.unlimited") : contractVsc?.termMonths?.toString(),
    },
    {
      label: t("vehicle.vehicleServiceContract.termMiles"),
      value: contractVsc?.unlimitedMileage ? t("vehicle.unlimited") : contractVsc?.termMiles?.toString(),
    },
    { label: t("vehicle.vehicleServiceContract.effectiveDate"), value: contractVsc?.effectiveDate },
    { label: t("vehicle.vehicleServiceContract.formNumber"), value: contractVsc?.formNo },
  ];

  if (!visible) {
    return null;
  }

  return (
    <div>
      <SubsectionHeader>
        {access.headerLink ? (
          <button
            ref={originalsTrigger}
            type="button"
            className="underline underline-offset-2"
            onClick={() => setOriginalsOpen(true)}
          >
            {t("vehicle.vehicleServiceContract.heading")}
          </button>
        ) : (
          t("vehicle.vehicleServiceContract.heading")
        )}
      </SubsectionHeader>
      <ProductOriginalsModal
        open={originalsOpen && access.headerLink}
        onOpenChange={setOriginalsOpen}
        triggerRef={originalsTrigger}
        title={t("vehicle.productOriginals.title", { product: t("vehicle.vehicleServiceContract.heading") })}
        rows={originalRows}
        unavailable={contractValuesUnavailable}
      />
      {unmappedMessages(unmapped).map((message) => (
        <p key={message} role="alert" className="text-warning text-2xs px-3 pt-2">
          {message}
        </p>
      ))}
      {formWarning && (
        <p role="alert" className="text-warning text-2xs px-3 pt-2">
          {formWarning}
        </p>
      )}
      {saveError && (
        <p role="alert" className="text-destructive text-2xs px-3 pt-2">
          {saveError}
        </p>
      )}
      <Form {...form}>
        <form onSubmit={(event) => event.preventDefault()} noValidate>
          <div className="gap-(--spacing-4) px-(--spacing-3) py-(--spacing-3) grid grid-cols-1 sm:grid-cols-2">
            {access.vendorDropdown ? (
              <DropdownField
                label={t("vehicle.vehicleServiceContract.vendor")}
                value={tpapVendor.field.value}
                onChange={handleVendorChange}
                options={tpapVendorOptions}
                error={tpapVendor.fieldState.error}
                disabled={!vendorDropdownEnabled}
              />
            ) : (
              <TextField
                label={t("vehicle.vehicleServiceContract.vendor")}
                value={vendorDisplay.field.value}
                error={tpapVendor.fieldState.error}
                disabled
              />
            )}
            <TextField
              label={t("vehicle.vehicleServiceContract.certificateNumber")}
              value={certificateNumber.field.value}
              onChange={certificateNumber.field.onChange}
              onBlur={certificateNumber.field.onBlur}
              error={certificateNumber.fieldState.error}
              disabled={!access.certificate}
              maxLength={11}
              uppercase
            />
            <CurrencyField
              label={t("vehicle.vehicleServiceContract.sellingPrice")}
              value={sellingPrice.field.value}
              onChange={(value) => sellingPrice.field.onChange(value ?? null)}
              onBlur={sellingPrice.field.onBlur}
              error={sellingPrice.fieldState.error}
              disabled={!access.sellingPrice}
            />
            {!tpapVsc.field.value && (
              <TextField
                label={t("vehicle.vehicleServiceContract.term")}
                value={termDisplay.field.value}
                error={termDisplay.fieldState.error}
                disabled
              />
            )}
            {tpapVsc.field.value && (
              <>
                <div className="gap-(--spacing-2) flex flex-col">
                  {isUnlimitedMonth.field.value ? (
                    <TextField
                      label={t("vehicle.vehicleServiceContract.termMonths")}
                      value=""
                      error={termMonths.fieldState.error}
                      disabled
                    />
                  ) : (
                    <TextField
                      label={t("vehicle.vehicleServiceContract.termMonths")}
                      value={termMonths.field.value}
                      onChange={(value) => termMonths.field.onChange(value.replaceAll(/\D/g, ""))}
                      onBlur={termMonths.field.onBlur}
                      error={termMonths.fieldState.error}
                      disabled={!access.terms}
                      maxLength={3}
                    />
                  )}
                  <CheckboxField
                    label={t("vehicle.vehicleServiceContract.unlimitedMonth")}
                    checked={isUnlimitedMonth.field.value}
                    onCheckedChange={handleUnlimitedMonthChange}
                    disabled={!access.unlimited}
                  />
                </div>
                <div className="gap-(--spacing-2) flex flex-col">
                  {isUnlimitedMile.field.value ? (
                    <TextField
                      label={t("vehicle.vehicleServiceContract.termMiles")}
                      value=""
                      error={termMiles.fieldState.error}
                      disabled
                    />
                  ) : (
                    <TextField
                      label={t("vehicle.vehicleServiceContract.termMiles")}
                      value={termMiles.field.value}
                      onChange={(value) => termMiles.field.onChange(value.replaceAll(/\D/g, ""))}
                      onBlur={termMiles.field.onBlur}
                      error={termMiles.fieldState.error}
                      disabled={!access.terms}
                      maxLength={6}
                    />
                  )}
                  <CheckboxField
                    label={t("vehicle.vehicleServiceContract.unlimitedMile")}
                    checked={isUnlimitedMile.field.value}
                    onCheckedChange={handleUnlimitedMileChange}
                    disabled={!access.unlimited}
                  />
                </div>
              </>
            )}
            <DateField
              label={t("vehicle.vehicleServiceContract.effectiveDate")}
              value={effectiveDate.field.value}
              onChange={effectiveDate.field.onChange}
              onBlur={effectiveDate.field.onBlur}
              error={effectiveDate.fieldState.error}
              disabled={!access.effectiveDate}
            />
            {tpapVsc.field.value && (
              <DropdownField
                label={t("vehicle.vehicleServiceContract.formNumber")}
                value={formNumber.field.value}
                onChange={handleFormNumberChange}
                onBlur={formNumber.field.onBlur}
                error={formNumber.fieldState.error}
                options={formNumberOptions}
                disabled={!access.formNumber}
              />
            )}
          </div>
        </form>
      </Form>
    </div>
  );
}
